import moment from "moment";
import { toastError } from "../components/toast";
import { countries } from "country-data";

export interface SelectMenuOption {
  value: string;
  title: string;
  code: string;
}

export const addPhoneNumberPrefix = (value: string, code: string) => {
  return code + value;
};

export const handleCommonDispatchActions = async ({
  dispatch,
  actionName,
  data,
  isDisableErrorToast = false,
}: any) => {
  const response = await dispatch(actionName?.(data));
  if (isDisableErrorToast) {
    handleApiErrorResponse({ ...response?.payload?.data });
  }
  return {
    data: response?.payload?.data,
    meta: response?.payload,
  };
};

export const handleApiErrorResponse = (response: any) => {
  if (response?.success === false && response?.error?.message) {
    toastError({ message: response?.error?.message });
  }
  return false;
};

export const getUserName = (user: any) => {
  const capitalize = (str: string) =>
    str?.charAt(0)?.toUpperCase() + str?.slice(1)?.toLowerCase();

  let name = "";

  if (user?.first_name) {
    name = capitalize(user.first_name);
  }

  if (user?.last_name) {
    name = name
      ? name + " " + capitalize(user.last_name)
      : capitalize(user.last_name);
  }

  return name;
};

export const conversionOfPostDate = (date: any) => {
  const now = moment();
  const postDate = moment(date);
  const differenceInMinutes = now.diff(postDate, "minutes");
  const differenceInHours = now.diff(postDate, "hours");
  const differenceInDays = now.diff(postDate, "days");

  if (differenceInMinutes < 1) {
    // Less than 1 minute ago
    return "Just now";
  } else if (differenceInMinutes < 60) {
    // 1 minute to less than 60 minutes ago
    return `${differenceInMinutes} minutes ago`;
  } else if (differenceInHours < 24) {
    // 1 hour to less than 24 hours ago
    return `${differenceInHours} hours ago`;
  } else if (differenceInHours < 48) {
    // Between 24 hours and 48 hours ago
    return `Yesterday at ${postDate.format("h:mm A")}`;
  } else if (differenceInDays < 7) {
    // More than 2 days but less than a week
    return `${differenceInDays} days ago`;
  } else if (differenceInDays < 365) {
    // More than 1 week but less than a year
    return postDate.format("MMM D");
  } else {
    // More than 1 year ago
    return postDate.format("MMM D, YYYY");
  }
};

export const getDayDifference = (date: any) => {
  const currentDate = moment(new Date());
  const postDate = moment(date);

  const differenceInMinutes = currentDate.diff(postDate, "minutes");
  const differenceInHours = currentDate.diff(postDate, "hours");
  const differenceInDays = currentDate.diff(postDate, "days");
  const differenceInWeeks = currentDate.diff(postDate, "weeks");
  const differenceInYears = currentDate.diff(postDate, "years");

  if (differenceInMinutes < 1) {
    // Less than 1 minute ago
    return "Just now";
  } else if (differenceInMinutes < 60) {
    // 1 minute to less than 1 hour ago
    return `${differenceInMinutes}m`;
  } else if (differenceInHours < 24) {
    // 1 hour to less than 24 hours ago
    return `${differenceInHours}h`;
  } else if (differenceInDays === 1) {
    // Yesterday (between 24 and 48 hours)
    return `1d`;
  } else if (differenceInDays <= 7) {
    // 2 to 7 days ago
    return `${differenceInDays}d`;
  } else if (differenceInDays > 7 && differenceInYears === 0) {
    // More than 7 days ago but less than a year
    return `${differenceInWeeks}w`;
  } else {
    // More than 1 year ago
    return `${differenceInYears}y`;
  }
};

export const generateUsername = (
  firstName: string,
  lastName: string,
): string => {
  // Remove spaces from the firstName and lastName
  const sanitizedFirstName = firstName.replace(/\s+/g, "");
  const sanitizedLastName = lastName.replace(/\s+/g, "");

  const baseUsername =
    `${sanitizedFirstName.toLowerCase()}${sanitizedLastName.toLowerCase()}`.slice(
      0,
      30,
    );

  const randomNumber = Math.floor(100 + Math.random() * 900);

  return `${baseUsername}${randomNumber}`;
};

const getCountries = (): SelectMenuOption[] => {
  return Object.values(countries.all)
    .filter(
      (country: any) =>
        country.alpha2 &&
        country.name &&
        country.countryCallingCodes &&
        country.countryCallingCodes.length > 0,
    )
    .map((country: any) => ({
      value: country.alpha2,
      title: country.name,
      code: country.countryCallingCodes[0].replace(/^\+/, "") || "",
    }));
};

export const COUNTRIES = getCountries();

export function formatLearnersCount(count: number) {
  if (count >= 1_000_000) {
    return `${(count / 1_000_000).toFixed(1)}M`;
  } else if (count >= 1_000) {
    return `${(count / 1_000).toFixed(1)}k`;
  } else {
    return count.toString();
  }
}

export const isPDF = (url: string) => {
  if (url.startsWith("blob:")) {
    return fetch(url)
      .then((response) => response.blob())
      .then((blob) => {
        const mimeType = blob.type;
        return mimeType === "application/pdf";
      });
  }
  return url.includes(".pdf");
};

export const formatFileSize = (sizeInBytes: number) => {
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  if (sizeInBytes === 0) return "0 Bytes";
  const i = Math.floor(Math.log(sizeInBytes) / Math.log(1024));
  return (
    parseFloat((sizeInBytes / Math.pow(1024, i)).toFixed(2)) + " " + sizes[i]
  );
};

export const formatAmount = (amount: number) => {
  return amount.toLocaleString("en-IN", {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  });
};

export const formatDuration = (duration: number) => {
  return `${Math.floor(duration / 60) === 0 ? `${duration % 60}m` : `${Math.floor(duration / 60)}h ${duration % 60}m`}`;
};

export const renderTextWithLinks = (text: string) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);

  return parts.map((part, index) => {
    if (urlRegex.test(part)) {
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "blue",
            textDecoration: "underline",
            wordBreak: "break-all",
          }}
        >
          {part}
        </a>
      );
    }
    return <span key={index}>{part}</span>;
  });
};

export const formatCurrency = (value: number, currency: string) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: currency,
  }).format(value);
};

export const getFYEndDate = (date: Date | string) => {
  const currentDate = moment(date);
  const year = currentDate.year();
  const month = currentDate.month();

  if (month > 3) {
    return moment()
      .set({ year: year + 1, month: 2, date: 31 })
      .format("YYYY-MM-DD");
  } else {
    return moment()
      .set({ year: year, month: 2, date: 31 })
      .format("YYYY-MM-DD");
  }
};
